// Athlete-side a11y/perf preference hooks (sess 19).
//
// useReducedMotion — respects OS `prefers-reduced-motion: reduce`. Use to gate
//   animations: countdowns, carousels, smooth scroll, micro-interactions.
//
// useDataMode — opt-in "saver" mode stored in localStorage. UI may choose to
//   skip heavy network calls (3D avatars, video previews, autoplay) when
//   "saver" is active. Default is "full".
//
// Both hooks update reactively when the source value changes (media query
// listener / storage event).

import { useEffect, useState } from "react";

const DATA_MODE_KEY = "storica.data_mode";

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    // Safari < 14 had addListener only; addEventListener is the modern API.
    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    } else {
      // @ts-ignore — legacy fallback
      mq.addListener(handler);
      // @ts-ignore — legacy fallback
      return () => mq.removeListener(handler);
    }
  }, []);
  return reduced;
}

export type DataMode = "full" | "saver";

function readDataMode(): DataMode {
  if (typeof window === "undefined") return "full";
  try {
    const v = window.localStorage.getItem(DATA_MODE_KEY);
    return v === "saver" ? "saver" : "full";
  } catch {
    return "full";
  }
}

export function useDataMode(): DataMode {
  const [mode, setMode] = useState<DataMode>(readDataMode);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: StorageEvent) => {
      if (e.key === DATA_MODE_KEY) setMode(readDataMode());
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);
  return mode;
}

export function setDataMode(mode: DataMode): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(DATA_MODE_KEY, mode);
    // Force same-tab listeners to update (storage event only fires across tabs)
    window.dispatchEvent(
      new StorageEvent("storage", { key: DATA_MODE_KEY, newValue: mode }),
    );
  } catch {
    // silent: localStorage unavailable (private mode / quota)
  }
}
